import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface ErrorLog {
  id: string;
  timestamp: string;
  createdAt: number;
  level: 'error' | 'warn' | 'info';
  message: string;
  stack?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ErrorLoggerService {
  private readonly storageKey = 'myfruit.error.logs';
  private readonly maxLogs = 200;
  private readonly maxMessageLength = 2000;
  private readonly retentionMs = 2 * 24 * 60 * 60 * 1000;

  private errors$ = new BehaviorSubject<ErrorLog[]>([]);
  private showErrorPanel$ = new BehaviorSubject<boolean>(false);

  constructor() {
    this.loadPersistedErrors();
    this.setupGlobalErrorHandlers();
  }

  get errors(): Observable<ErrorLog[]> {
    return this.errors$.asObservable();
  }

  get showErrorPanel(): Observable<boolean> {
    return this.showErrorPanel$.asObservable();
  }

  private setupGlobalErrorHandlers(): void {
    // Catch console errors
    const originalError = console.error;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    console.error = (...args: any[]) => {
      originalError.apply(console, args);
      this.scheduleLog('error', args.join(' '));
    };

    const originalWarn = console.warn;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    console.warn = (...args: any[]) => {
      originalWarn.apply(console, args);
      this.scheduleLog('warn', args.join(' '));
    };

    const originalLog = console.log;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    console.log = (...args: any[]) => {
      originalLog.apply(console, args);
      this.scheduleLog('info', this.formatArgs(args));
    };

    // Catch unhandled errors
    window.addEventListener('error', (event) => {
      this.scheduleLog(
        'error',
        `${event.message} (${event.filename}:${event.lineno})`,
      );
    });

    // Catch unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.scheduleLog('error', `Unhandled Promise: ${event.reason}`);
    });
  }

  private scheduleLog(level: 'error' | 'warn' | 'info', message: string): void {
    setTimeout(() => {
      this.logError(level, message);
    }, 0);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private formatArgs(args: any[]): string {
    return args
      .map((arg) => {
        if (typeof arg === 'string') {
          return arg;
        }

        try {
          return JSON.stringify(arg);
        } catch {
          return String(arg);
        }
      })
      .join(' ');
  }

  private logError(level: 'error' | 'warn' | 'info', message: string): void {
    const now = Date.now();
    const errorLog: ErrorLog = {
      id: this.createLogId(now),
      timestamp: new Date(now).toLocaleString(),
      createdAt: now,
      level,
      message: this.limitMessageSize(message),
    };

    const currentErrors = this.pruneExpiredLogs(this.errors$.value);
    const newErrors = [errorLog, ...currentErrors].slice(0, this.maxLogs);
    this.errors$.next(newErrors);
    this.persistErrors(newErrors);
  }

  toggleErrorPanel(): void {
    this.showErrorPanel$.next(!this.showErrorPanel$.value);
  }

  openErrorPanel(): void {
    this.showErrorPanel$.next(true);
  }

  clearErrors(): void {
    this.errors$.next([]);
    this.clearPersistedErrors();
  }

  private loadPersistedErrors(): void {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }

    try {
      const raw = localStorage.getItem(this.storageKey);
      if (!raw) {
        return;
      }

      const parsed: unknown = JSON.parse(raw);
      if (!Array.isArray(parsed)) {
        this.clearPersistedErrors();
        return;
      }

      const normalizedLogs = parsed
        .filter((log) => typeof log === 'object' && log !== null)
        .map((log, index): ErrorLog => {
          const candidate = log as Record<string, unknown>;

          const createdAt =
            typeof candidate['createdAt'] === 'number'
              ? candidate['createdAt']
              : Date.now();

          const levelValue = candidate['level'];
          const level: ErrorLog['level'] =
            levelValue === 'error' ||
            levelValue === 'warn' ||
            levelValue === 'info'
              ? levelValue
              : 'info';

          const message =
            typeof candidate['message'] === 'string'
              ? candidate['message']
              : String(candidate['message'] ?? '');

          const timestamp =
            typeof candidate['timestamp'] === 'string'
              ? candidate['timestamp']
              : new Date(createdAt).toLocaleString();

          const id =
            typeof candidate['id'] === 'string' && candidate['id'].length > 0
              ? candidate['id']
              : this.createLogId(createdAt, index);

          return {
            id,
            timestamp,
            createdAt,
            level,
            message: this.limitMessageSize(message),
          };
        })
        .slice(0, this.maxLogs);

      const freshLogs = this.pruneExpiredLogs(normalizedLogs);
      this.errors$.next(freshLogs);
      this.persistErrors(freshLogs);
    } catch {
      this.clearPersistedErrors();
    }
  }

  private persistErrors(errors: ErrorLog[]): void {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }

    try {
      localStorage.setItem(this.storageKey, JSON.stringify(errors));
    } catch {
      const trimmed = errors.slice(0, Math.ceil(this.maxLogs / 2));
      try {
        localStorage.setItem(this.storageKey, JSON.stringify(trimmed));
        this.errors$.next(trimmed);
      } catch {
        this.clearPersistedErrors();
      }
    }
  }

  private clearPersistedErrors(): void {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }

    localStorage.removeItem(this.storageKey);
  }

  private pruneExpiredLogs(errors: ErrorLog[]): ErrorLog[] {
    const now = Date.now();
    return errors.filter((error) => now - error.createdAt <= this.retentionMs);
  }

  private limitMessageSize(message: string): string {
    if (message.length <= this.maxMessageLength) {
      return message;
    }

    return `${message.slice(0, this.maxMessageLength)}…`;
  }

  private createLogId(createdAt: number, seed?: number): string {
    if (typeof seed === 'number') {
      return `${createdAt}-${seed}`;
    }

    return `${createdAt}-${Math.random().toString(36).slice(2, 10)}`;
  }
}
